import { EntityType, EntityUpdateUnion } from 'api'
import { DataHelper } from './DataHelperTypes'
import {
  AlertEntityHelper,
  AuditEntryHelper,
  AuthorisedUserHelper,
  AxeEntityHelper,
  BatchDataHelper,
  BestPriceHelper,
  ClearingHouseHelper,
  ClearingHouseInteractionHelper,
  ClusterClientStateHelper,
  ClusterPongHelper,
  ClusterSessionHelper,
  CollateralBasketHelper,
  CollateralDetailsHelper,
  CollateralHelper,
  CollateralPriceHelper,
  CollateralUploadHelper,
  CollectionHelper,
  ComplianceCollateralHelper,
  CounterHelper,
  CountryHelper,
  DatabaseReportGatewayRequestHelper,
  DealExportHelper,
  DealHelper,
  DevSignalHelper,
  EgressPositionHelper,
  EoniaRateHelper,
  EstrRateHelper,
  ExportFailedRetryDataHelper,
  FatFingerProductSectorLimitHelper,
  FatFingerUserLimitHelper,
  FxRateHelper,
  HaircutBandHelper,
  HaircutCollateralBandHelper,
  HaircutEntryHelper,
  HolidayHelper,
  ImportCompleteHelper,
  IndexHelper,
  InFlightOrganisationHelper,
  InFlightTargetOrganisationHelper,
  IssuerOrganisationHelper,
  NegotiationAggregateHelper,
  NegotiationHelper,
  NegotiationHistoryHelper,
  NegotiationOverrideHelper,
  NotesHelper,
  OrderHelper,
  OrderRecordKeepingHelper,
  OrganisationHelper,
  OrganisationMetaHelper,
  PermissionReportRequestHelper,
  PlatformHelper,
  PrimeBrokerHelper,
  ProductHelper,
  ProductSectorHelper,
  ProductSectorMappingDraftHelper,
  ProductSectorMappingHelper,
  ProductSectorMappingStateHelper,
  QueryRequestHelper,
  ReferenceDataManagementDataHelper,
  ReferenceRateHelper,
  RefinitivCollateralImportResultHelper,
  SotwPositionHelper,
  SotwResetHelper,
  SponsorHelper,
  SpreadsheetMappingHelper,
  TradingControlLimitHelper,
  TradingControlLimitUserUtilisationHelper,
  TradingEntityClearingHelper,
  TradingEntityHelper,
  TradingEntityMetaHelper,
  TradingRelationshipHelper,
  UserHelper,
  UserPreferenceHelper,
  UserSettingsHelper,
  WorkingDaysHelper
} from './DataHelpers'

export type EntityUpdateTypeFor<TEntityType extends EntityType> = Extract<EntityUpdateUnion, Record<'entityType', TEntityType>>
export type EntityTypeFor<TEntityType extends EntityType> = EntityUpdateTypeFor<TEntityType>['entity']

type EntityHelperMap = { [TEntityType in EntityType]: DataHelper<EntityTypeFor<TEntityType>> }

export const EntityHelpers: EntityHelperMap = {
  [EntityType.APPLICATION_ALERT]: AlertEntityHelper,
  [EntityType.AXE]: AxeEntityHelper,
  [EntityType.AUDIT_ENTRY]: AuditEntryHelper,
  [EntityType.AUTHORISED_USER]: AuthorisedUserHelper,
  [EntityType.BATCH_DATA]: BatchDataHelper,
  [EntityType.BEST_PRICE]: BestPriceHelper,
  [EntityType.CLEARING_HOUSE]: ClearingHouseHelper,
  [EntityType.CLEARING_HOUSE_INTERACTION]: ClearingHouseInteractionHelper,
  [EntityType.COLLATERAL]: CollateralHelper,
  [EntityType.COLLATERAL_DETAILS]: CollateralDetailsHelper,
  [EntityType.COLLATERAL_PRICE]: CollateralPriceHelper,
  [EntityType.COLLATERAL_BASKET]: CollateralBasketHelper,
  [EntityType.COLLATERAL_UPLOAD]: CollateralUploadHelper,
  [EntityType.COLLATERAL_REFINITIV_IMPORT_RESULT]: RefinitivCollateralImportResultHelper,
  [EntityType.COMPLIANCE_COLLATERAL]: ComplianceCollateralHelper,
  [EntityType.IMPORT_COMPLETE]: ImportCompleteHelper,
  [EntityType.COUNTER]: CounterHelper,
  [EntityType.COUNTRY]: CountryHelper,
  [EntityType.COLLECTION]: CollectionHelper,
  [EntityType.CLUSTER_PONG]: ClusterPongHelper,
  [EntityType.CLUSTER_CLIENT_STATE]: ClusterClientStateHelper,
  [EntityType.CLUSTER_SESSION]: ClusterSessionHelper,
  [EntityType.DATABASE_REPORT_REQUEST]: DatabaseReportGatewayRequestHelper,
  [EntityType.DEAL]: DealHelper,
  [EntityType.DEAL_INTEREST_PERIOD]: DealHelper,
  [EntityType.DEAL_HISTORY]: DealHelper,
  [EntityType.DEAL_EXPORT]: DealExportHelper,
  [EntityType.DEV_SIGNAL]: DevSignalHelper,
  [EntityType.EGRESS_POSITION]: EgressPositionHelper,
  [EntityType.EONIA_RATE]: EoniaRateHelper,
  [EntityType.ESTR_RATE]: EstrRateHelper,
  [EntityType.FAT_FINGER_PRODUCT_SECTOR]: FatFingerProductSectorLimitHelper,
  [EntityType.FAT_FINGER_USER]: FatFingerUserLimitHelper,
  [EntityType.FX_RATE]: FxRateHelper,
  [EntityType.HAIRCUT_BAND]: HaircutBandHelper,
  [EntityType.HAIRCUT_COLLATERAL_BAND]: HaircutCollateralBandHelper,
  [EntityType.HAIRCUT_ENTRY]: HaircutEntryHelper,
  [EntityType.HOLIDAY]: HolidayHelper,
  [EntityType.INDEX]: IndexHelper,
  [EntityType.IN_FLIGHT_ORGANISATION]: InFlightOrganisationHelper,
  [EntityType.IN_FLIGHT_TARGET_ORGANISATION]: InFlightTargetOrganisationHelper,
  [EntityType.NEGOTIATION]: NegotiationHelper,
  [EntityType.NEGOTIATION_AGGREGATE]: NegotiationAggregateHelper,
  [EntityType.NEGOTIATION_OVERRIDE]: NegotiationOverrideHelper,
  [EntityType.NEGOTIATION_HISTORY]: NegotiationHistoryHelper,
  [EntityType.NOTES]: NotesHelper,
  [EntityType.ORDER]: OrderHelper,
  [EntityType.ORDER_RECORD_KEEPING]: OrderRecordKeepingHelper,
  [EntityType.ORGANISATION]: OrganisationHelper,
  [EntityType.ISSUER_ORGANISATION]: IssuerOrganisationHelper,
  [EntityType.ORGANISATION_META]: OrganisationMetaHelper,
  [EntityType.PERMISSION_REPORT_REQUEST]: PermissionReportRequestHelper,
  [EntityType.PLATFORM]: PlatformHelper,
  [EntityType.PRODUCT]: ProductHelper,
  [EntityType.PRIME_BROKER]: PrimeBrokerHelper,
  [EntityType.PRODUCT_SECTOR]: ProductSectorHelper,
  [EntityType.PRODUCT_SECTOR_MAPPING]: ProductSectorMappingHelper,
  [EntityType.PRODUCT_SECTOR_MAPPING_DRAFT]: ProductSectorMappingDraftHelper,
  [EntityType.PRODUCT_SECTOR_MAPPING_STATE]: ProductSectorMappingStateHelper,
  [EntityType.QUERY_REQUEST]: QueryRequestHelper,
  [EntityType.REFERENCE_DATA_MANAGEMENT_DATA]: ReferenceDataManagementDataHelper,
  [EntityType.REFERENCE_RATE]: ReferenceRateHelper,
  [EntityType.RETRY_FAILED_EXPORT]: ExportFailedRetryDataHelper,
  [EntityType.RESET_SOTW]: SotwResetHelper,
  [EntityType.SOTW_SENT]: SotwPositionHelper,
  [EntityType.SPONSOR]: SponsorHelper,
  [EntityType.SPREADSHEET_MAPPING]: SpreadsheetMappingHelper,
  [EntityType.TRADING_CONTROL_LIMIT]: TradingControlLimitHelper,
  [EntityType.TRADING_CONTROL_LIMIT_USER_UTILISATION]: TradingControlLimitUserUtilisationHelper,
  [EntityType.TRADING_ENTITY]: TradingEntityHelper,
  [EntityType.TRADING_ENTITY_CLEARING]: TradingEntityClearingHelper,
  [EntityType.TRADING_ENTITY_META]: TradingEntityMetaHelper,
  [EntityType.TRADING_RELATIONSHIP]: TradingRelationshipHelper,
  [EntityType.USER]: UserHelper,
  [EntityType.USER_PREFERENCE]: UserPreferenceHelper,
  [EntityType.USER_SETTINGS]: UserSettingsHelper,
  [EntityType.WORKING_DAYS]: WorkingDaysHelper
}
